import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";
import { Icon, theme } from "uikit";

import Call from "../../../../services/Call";
import {
	CallStatus,
	formatDuration,
	formatStart,
	getDirection,
	getDispatcherName,
	getPartyName,
	getStartTime,
	getStatus,
	getTimings,
	isLive,
} from "../../utils";
import CallRecord from "../Record";

const path = "change_history.calls";

const STATUS_COLORS: Record<CallStatus, { color: string; background: string }> =
	{
		accepted: { color: "#2E7D32", background: "#DBEFDC" },
		rejected: { color: "#F83528", background: "#FFE3E3" },
		missed: { color: "#F83528", background: "#FFE3E3" },
	};

const ICON_COLORS: Record<CallStatus, string> = {
	accepted: theme.colors.success,
	rejected: theme.colors.negative,
	missed: theme.colors.negative,
};

/** Still ringing, the call has no status yet. */
const RINGING_ICON_COLOR = "#E0A800";

const iconColor = (status: CallStatus | null, live: boolean): string => {
	if (status) return ICON_COLORS[status];
	return live ? RINGING_ICON_COLOR : theme.colors.disabled_text;
};

const pulse = keyframes`
	0% { opacity: 1; }
	50% { opacity: 0.3; }
	100% { opacity: 1; }
`;

const Root = styled.div`
	display: grid;
	grid-template-columns: 24px minmax(0, 1fr) auto auto;
	grid-template-areas:
		"icon title chip record"
		"icon details details record"
		"icon timings timings record";
	align-items: center;
	gap: 3px 12px;
	padding: 10px 12px;
	border-bottom: 1px solid ${theme.colors.color_border_basic};
	background: #ffffff;

	&:hover {
		background: #f8fafb;
	}
`;

const Direction = styled.span`
	grid-area: icon;
	align-self: start;
	padding-top: 2px;
`;

const Ellipsis = css`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const Title = styled.div`
	grid-area: title;
	min-width: 0;
	${Ellipsis}
	font-size: 13px;
	line-height: 16px;
	color: ${theme.colors.primary};

	b {
		font-weight: 600;
	}

	span {
		color: ${theme.colors.secondary};
		margin-left: 6px;
	}
`;

const Details = styled.div`
	grid-area: details;
	min-width: 0;
	${Ellipsis}
	font-size: 12px;
	line-height: 15px;
	color: ${theme.colors.secondary};
`;

const Chip = styled.div<{ color: string; background: string }>`
	grid-area: chip;
	justify-self: end;
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 2px 8px;
	border-radius: 10px;
	font-size: 11px;
	font-weight: 600;
	line-height: 14px;
	white-space: nowrap;
	color: ${({ color }) => color};
	background: ${({ background }) => background};
`;

const LiveDot = styled.i<{ color: string }>`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: ${({ color }) => color};
	animation: ${pulse} 1.2s ease-in-out infinite;
`;

const Timings = styled.div`
	grid-area: timings;
	font-size: 12px;
	line-height: 15px;
	color: ${theme.colors.secondary};
	white-space: nowrap;
	font-variant-numeric: tabular-nums;

	b {
		font-weight: 500;
		color: ${theme.colors.primary};
	}
`;

const RecordCell = styled.div`
	grid-area: record;
`;

const Item: React.FC<Item.Props> = ({ call, now }) => {
	const { t } = useTranslation();

	const status = getStatus(call, now);
	const direction = getDirection(call);
	const { wait, talk } = getTimings(call, now);
	const live = isLive(call, now);

	const party = getPartyName(call);
	const dispatcher = getDispatcherName(call);
	const details = [
		formatStart(getStartTime(call)),
		call.operator ? `SIP ${call.operator}` : "",
		dispatcher,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<Root>
			<Direction title={t(`${path}.direction.${direction}`) || ""}>
				<Icon
					id={
						direction === "outgoing"
							? "phone-call-outgoing"
							: "phone-call-incoming"
					}
					size={22}
					colors={[iconColor(status, live)]}
				/>
			</Direction>

			<Title title={[call.phone, party].filter(Boolean).join(" ")}>
				<b>{call.phone || "—"}</b>
				{party && <span>{party}</span>}
			</Title>

			{status && (
				<Chip
					color={STATUS_COLORS[status].color}
					background={STATUS_COLORS[status].background}
				>
					{live && <LiveDot color={STATUS_COLORS[status].color} />}
					{t(`${path}.result.${status}`)}
				</Chip>
			)}

			<Details title={details}>{details}</Details>

			<Timings>
				{t(`${path}.waitTime`)} <b>{formatDuration(wait)}</b>
				{" · "}
				{t(`${path}.talkTime`)} <b>{formatDuration(talk)}</b>
			</Timings>

			<RecordCell>
				<CallRecord playSoundUrl={call.playSoundUrl} />
			</RecordCell>
		</Root>
	);
};

declare namespace Item {
	interface Props {
		call: Call.Model;
		now: number;
	}
}

export default memo(Item);
